import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import type { UpsertProductDto } from '../types/ProductDto';
import {
	validateNumber,
	validateText,
} from '../../../util/validation/commonValidation';
import { useVatRate } from '../../vat-rates/hooks/useVatRate';
import { useExcVatAutoCalc } from './useExcVatAutoCalc';
import {
	makePriceValidationRules,
	withZeroPriceDefaults,
} from './useUpsertPriceForm';

const defaultProduct: UpsertProductDto = {
	description: '',
	skuid: '',
	brand: '',
	model: '',
	groupId: '',
	departmentId: '',
	supplierId: '',
	vatRateId: '',
	barcode: '',
	prices: {
		...withZeroPriceDefaults(),
		// Blank on new products so the user is prompted to enter them
		priceIncVatOne: undefined,
		priceExcVatOne: undefined,
	},
};

export function useUpsertProductForm(product?: UpsertProductDto) {
	// No product means a new one is being created: prices start blank and are
	// required. Existing products keep lenient rules so empties stay valid.
	const isNewProduct = product === undefined;
	const baseProduct = product ?? defaultProduct;

	const initialProduct: UpsertProductDto = {
		...baseProduct,
		prices: baseProduct.prices ?? {}, // Must be an object for nested form paths to be settable
	};

	const [inputs, setInputs] = useState<UpsertProductDto>(initialProduct);

	const form = useForm<UpsertProductDto>({
		mode: 'uncontrolled',
		initialValues: initialProduct,
		validate: {
			prices: makePriceValidationRules(!isNewProduct),
			description: (value) => validateText(value, false, 'Description', 50),
			skuid: (value) => validateText(value, false, 'SKUID', 25),
			barcode: (value) => validateText(value, true, 'Master barcode', 25),
			brand: (value) => validateText(value, true, 'Brand', 50),
			model: (value) => validateText(value, true, 'Model', 30),
			groupId: (value) => validateText(value, false, 'Group', 15),
			departmentId: (value) =>
				validateNumber(value, false, 0, 10000000, 'Department', false),
			supplierId: (value) => validateText(value, false, 'Supplier', 15),
			vatRateId: (value) => validateText(value, false, 'VAT Rate', 10),
			caseCost: (value) =>
				validateNumber(
					value?.toString(),
					false,
					0,
					10000000,
					'Case cost',
					true,
				),
			singleCost: (value) =>
				validateNumber(
					value?.toString(),
					false,
					0,
					10000000,
					'Single cost',
					true,
				),
			caseQuantity: (value) =>
				validateNumber(
					value?.toString(),
					false,
					0,
					1000000,
					'Case quantity',
					false,
				),
			stockQuantity: (value) =>
				validateNumber(
					value?.toString(),
					false,
					-1000000,
					10000000,
					'Minimum Stock',
					false,
				),
			orderQuantity: (value) =>
				validateNumber(
					value?.toString(),
					false,
					0,
					1000000,
					'Order quantity',
					false,
				),
		},
		onValuesChange: (v) => setInputs(v),
	});

	const vatRate = useVatRate(inputs.vatRateId);

	useExcVatAutoCalc(form, inputs.prices, vatRate, 'prices.');

	useEffect(() => {
		if (inputs.caseCost && inputs.caseQuantity) {
			form.setFieldValue('singleCost', inputs.caseCost / inputs.caseQuantity);
		}
	}, [inputs.caseCost, inputs.caseQuantity]);

	return { form, inputs };
}
